import { Suspense, lazy } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './auth/AuthProvider'
import { AppShell } from './components/layout/AppShell'
import { ModuleGuard } from './auth/ModuleGuard'
import LoginPage from './pages/Login'
import ChangePasswordPage from './pages/ChangePassword'
import { landingPath } from './auth/landing'

/**
 * Home, for whoever is signed in.
 *
 * Was a hardcoded /dashboard in three places, which held while every role
 * could read one. The HR role cannot — it has no report.operational by
 * design — so it would have landed on a screen whose every request 403s.
 */
function HomeRedirect() {
  const { user } = useAuth()
  return <Navigate to={landingPath(user)} replace />
}


/**
 * Every screen past the dashboard is loaded on demand.
 *
 * The whole app in one file was 1.28 MB. On a Lebanese connection that is the
 * difference between the login screen appearing and someone giving up, and
 * most users never open the laboratory or donor-project modules at all.
 */
const DashboardPage = lazy(() => import('./pages/Dashboard'))
const TodayPage = lazy(() => import('./pages/Today'))
const CompaniesPage = lazy(() => import('./pages/Companies'))
const CompanyDetailPage = lazy(() => import('./pages/CompanyDetail'))
const ContactsPage = lazy(() => import('./pages/Contacts'))
const WhatsAppPage = lazy(() => import('./pages/WhatsApp'))
const StaffPage = lazy(() => import('./pages/Staff'))
const LeavePage = lazy(() => import('./pages/Leave'))
const TimesheetsPage = lazy(() => import('./pages/Timesheets'))
const ExpensesPage = lazy(() => import('./pages/Expenses'))
const PayablesPage = lazy(() => import('./pages/Payables'))
const PurchasesPage = lazy(() => import('./pages/Purchases'))
const InvoiceDetailPage = lazy(() => import('./pages/InvoiceDetail'))
const QuotationDetailPage = lazy(() => import('./pages/QuotationDetail'))
const PurchaseDetailPage = lazy(() => import('./pages/PurchaseDetail'))
const FollowUpDealsPage = lazy(() => import('./pages/FollowUpDeals'))
const UnderImplementationPage = lazy(() => import('./pages/UnderImplementation'))
const ClosedDealsPage = lazy(() => import('./pages/ClosedDeals'))
const DealDetailPage = lazy(() => import('./pages/DealDetail'))
const ImportPage = lazy(() => import('./pages/Import'))
const DuplicatesPage = lazy(() => import('./pages/Duplicates'))
const AuditsPage = lazy(() => import('./pages/Audits'))
const FindingsPage = lazy(() => import('./pages/Findings'))
const CertificatesPage = lazy(() => import('./pages/Certificates'))
const CoursesPage = lazy(() => import('./pages/Courses'))
const SessionsPage = lazy(() => import('./pages/Sessions'))
const SessionDetailPage = lazy(() => import('./pages/SessionDetail'))
const AuditDetailPage = lazy(() => import('./pages/AuditDetail'))
const TraineesPage = lazy(() => import('./pages/Trainees'))
const LabSamplesPage = lazy(() => import('./pages/LabSamples'))
const LabSampleDetailPage = lazy(() => import('./pages/LabSampleDetail'))
const FollowUpsPage = lazy(() => import('./pages/FollowUps'))
const ProductsPage = lazy(() => import('./pages/Products'))
const ProductDetailPage = lazy(() => import('./pages/ProductDetail'))
const SuppliersPage = lazy(() => import('./pages/Suppliers'))
const ProjectsPage = lazy(() => import('./pages/Projects'))
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetail'))
const QuotationsPage = lazy(() => import('./pages/Quotations'))
const InvoicesPage = lazy(() => import('./pages/Invoices'))
const CreditNotesPage = lazy(() => import('./pages/CreditNotes'))
const CreditNoteDetailPage = lazy(() => import('./pages/CreditNoteDetail'))
const ReportsPage = lazy(() => import('./pages/Reports'))
const TeamPage = lazy(() => import('./pages/Team'))
const AuditTrailPage = lazy(() => import('./pages/AuditTrail'))

function Spinner({ full }: { full?: boolean }) {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: full ? '100dvh' : 320 }}>
      <CircularProgress aria-label="Loading" />
    </Box>
  )
}

export default function App() {
  const { user, loading } = useAuth()

  if (loading) return <Spinner full />
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // A temporary password blocks everything else until it is replaced.
  if (user.mustChangePassword) {
    return (
      <Routes>
        <Route path="*" element={<ChangePasswordPage />} />
      </Routes>
    )
  }

  return (
    <AppShell>
      <ModuleGuard>
        <Suspense fallback={<Spinner />}>
          <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/today" element={<TodayPage />} />

          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/:id" element={<CompanyDetailPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/whatsapp" element={<WhatsAppPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/leave" element={<LeavePage />} />
          <Route path="/timesheets" element={<TimesheetsPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/payables" element={<PayablesPage />} />
          <Route path="/purchases" element={<PurchasesPage />} />
          <Route path="/purchases/:id" element={<PurchaseDetailPage />} />
          <Route path="/deals/follow-ups" element={<FollowUpDealsPage />} />
          <Route path="/deals/implementation" element={<UnderImplementationPage />} />
          <Route path="/deals/closed" element={<ClosedDealsPage />} />
          <Route path="/deals/:id" element={<DealDetailPage />} />
          <Route path="/import" element={<ImportPage />} />
          <Route path="/duplicates" element={<DuplicatesPage />} />

          <Route path="/audits" element={<AuditsPage />} />
          <Route path="/findings" element={<FindingsPage />} />
          <Route path="/certificates" element={<CertificatesPage />} />

          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/sessions" element={<SessionsPage />} />
          <Route path="/sessions/:id" element={<SessionDetailPage />} />
          <Route path="/audits/:id" element={<AuditDetailPage />} />
          <Route path="/trainees" element={<TraineesPage />} />

          <Route path="/lab" element={<LabSamplesPage />} />
          <Route path="/lab/:id" element={<LabSampleDetailPage />} />
          <Route path="/activity" element={<FollowUpsPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/suppliers" element={<SuppliersPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />

          <Route path="/quotations" element={<QuotationsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="/quotations/:id" element={<QuotationDetailPage />} />
          <Route path="/credit-notes" element={<CreditNotesPage />} />
          <Route path="/credit-notes/:id" element={<CreditNoteDetailPage />} />

          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/audit-trail" element={<AuditTrailPage />} />

          <Route path="*" element={<HomeRedirect />} />
        </Routes>
        </Suspense>
      </ModuleGuard>
    </AppShell>
  )
}
